import {
  ProductSubTier,
  ProductTier,
  PurchaseableTier,
  SubscriptionPlanName,
} from '@sparemin/api-sdk';
import React from 'react';
import {
  MultiVideoCamera,
  VideoCameraAlt,
  VideoCameraAlt2,
} from 'components/icons';
import { InfoboxProps } from 'components/Infobox';
import bem from 'utils/bem';
import { isProGrowSubTier } from 'utils/pricing';
import { BillingCyclePlanOptions } from '../BillingCycleModal/BillingCycleOptions';

export const block = bem('add-billing-cycle-modal');

interface InfoboxConfigValue extends Pick<InfoboxProps, 'icon' | 'theme'> {
  displayName: string;
}

const getInfoBoxConfigForTier = (
  tier: PurchaseableTier,
): InfoboxConfigValue | undefined => {
  switch (tier) {
    case ProductTier.BASIC:
      return {
        theme: 'purple',
        icon: <VideoCameraAlt2 />,
        displayName: 'Headliner Basic',
      };

    case ProductTier.PRO:
      return {
        theme: 'blue',
        icon: <MultiVideoCamera />,
        displayName: 'Headliner Pro',
      };

    case ProductTier.UNLIMITED:
      return {
        theme: 'gradient',
        icon: <VideoCameraAlt style={{ marginLeft: 3 }} />,
        displayName: 'Headliner Unlimited',
      };

    default:
      return undefined;
  }
};

const getInfoBoxConfigForSubtier = (
  subtier: ProductSubTier,
): InfoboxConfigValue | undefined => {
  return isProGrowSubTier(subtier)
    ? {
        theme: 'purple',
        icon: <VideoCameraAlt2 />,
        displayName: 'Headliner Pro + Grow',
      }
    : undefined;
};

export const getInfoBoxConfig = (
  tier: PurchaseableTier,
  subtier?: ProductSubTier,
): InfoboxConfigValue => {
  const tierConfig = getInfoBoxConfigForTier(tier);
  const subtierConfig = getInfoBoxConfigForSubtier(subtier);

  if (subtierConfig) {
    return subtierConfig;
  }

  return tierConfig;
};

const getPlanOptionsForTier = (
  tier: PurchaseableTier,
): BillingCyclePlanOptions => {
  switch (tier) {
    case ProductTier.BASIC:
      return {
        monthly: SubscriptionPlanName.BASIC_20250818_MONTHLY,
        yearly: SubscriptionPlanName.BASIC_20250818_YEARLY,
      };

    case ProductTier.PRO:
      return {
        monthly: SubscriptionPlanName.PRO_20250818_MONTHLY,
        yearly: SubscriptionPlanName.PRO_20250818_YEARLY,
      };

    case ProductTier.UNLIMITED:
      return {
        monthly: SubscriptionPlanName.UNLIMITED_20211112_MONTHLY,
        yearly: SubscriptionPlanName.UNLIMITED_20211112_YEARLY,
      };

    default:
      return undefined;
  }
};

const getPlanOptionsForSubtier = (
  subtier: ProductSubTier,
): BillingCyclePlanOptions => {
  return isProGrowSubTier(subtier)
    ? {
        monthly: SubscriptionPlanName.PRO_GROW_20251120_MONTHLY,
        yearly: SubscriptionPlanName.PRO_GROW_20251120_YEARLY,
      }
    : undefined;
};

export const getPlanOptions = (
  tier: PurchaseableTier,
  subtier?: ProductSubTier,
): BillingCyclePlanOptions => {
  const tierOptions = getPlanOptionsForTier(tier);
  const subtierOptions = getPlanOptionsForSubtier(subtier);

  if (subtierOptions) {
    return subtierOptions;
  }

  return tierOptions;
};
